'use client';

import { useLenis } from 'lenis/react';
import { motion, useMotionValue, useTransform } from 'motion/react';
import { useCallback, useRef } from 'react';

import { cn } from '~/lib/utils/classname';

import useTimelineStore, { TimelineSection } from '../../lib/store/timeline';

type Props = {
  isStatic?: boolean;
};

const Timeline: React.FC<Props> = ({ isStatic }) => {
  const sections = useTimelineStore((state) => state.sections);
  const progressValue = useMotionValue(0);

  const totalBounds = useRef<DOMRect>(null);
  const checkpoints = useRef<number[]>([]);

  const checkpointButtons = useRef<HTMLButtonElement[]>([]);

  const computeCheckpointsAndBounds = useCallback(() => {
    if (!isStatic) {
      const bounds = sections
        .map((section) => section.ref.current)
        .filter((element) => !!element)
        .map((element) => element.getBoundingClientRect())
        .sort((a, b) => a.left - b.left);

      const first = bounds.at(0);
      const last = bounds.at(-1);

      if (first && last) {
        return {
          bounds:
            first === last
              ? first
              : new DOMRect(
                  first.x + first.width / 2,
                  first.y,
                  last.left -
                    first.left +
                    last.width -
                    first.width / 2 -
                    last.width / 2,
                  first.height
                ),

          checkpoints: bounds.map((bounds) => bounds.left + bounds.width / 2),
        };
      }
    }

    return { checkpoints: [] };
  }, [isStatic, sections]);

  const progressMapFrom =
    checkpoints.current.length > 0
      ? checkpoints.current.map(
          (x) =>
            (x - (totalBounds.current?.left || 0)) /
            (totalBounds.current?.width || 1)
        )
      : [0, 1];

  const progressMapTo =
    checkpoints.current.length > 0
      ? checkpoints.current.map(
          (_x, index, array) => index / (array.length - 1)
        )
      : [0, 1];

  const mappedProgress = useTransform(
    progressValue,
    progressMapFrom,
    progressMapTo
  );

  const lenis = useLenis(
    (lenis) => {
      if (!isStatic) {
        const { innerWidth } = window;
        const { bounds: newBounds, checkpoints: newCheckpoints } =
          computeCheckpointsAndBounds();

        if (newBounds) {
          const progress = Math.min(
            Math.max(-1, -(newBounds.left - innerWidth / 2)) / newBounds.width,
            1
          );

          const isWithinTimelineBounds =
            lenis.actualScroll <
            lenis.dimensions.scrollWidth - lenis.dimensions.width * 2;

          progressValue.set(progress);

          totalBounds.current = newBounds;
          checkpoints.current = newCheckpoints;

          if (checkpointButtons.current) {
            checkpointButtons.current.forEach((button, index) => {
              if (
                isWithinTimelineBounds &&
                Math.max(0, progressMapFrom[index] - 0.025) <= progress
              ) {
                button.ariaCurrent = 'step';
              } else {
                button.ariaCurrent = null;
              }
            });
          }
        }
      }
    },
    [isStatic, progressMapFrom]
  );

  const scrollIntoView = (section: TimelineSection) => {
    if (section.ref.current && lenis) {
      const { innerWidth } = window;
      const elementWidth = section.ref.current.offsetWidth;
      const offset = -(innerWidth - elementWidth) / 2;

      lenis.scrollTo(section.ref.current, {
        duration: 1.25,
        lock: true,
        offset,
      });
    }
  };

  return (
    <>
      <div className="grid items-center py-1">
        <div
          className="pointer-events-none relative col-start-1 row-start-1 flex px-10"
          aria-hidden
        >
          <div className="h-px w-full bg-neutral-200">
            <motion.div
              className="bg-brand-primary relative h-full w-full origin-left"
              style={{ scaleX: isStatic ? 0 : mappedProgress }}
            />
          </div>
        </div>

        <div className="relative col-start-1 row-start-1 flex w-full flex-row items-center justify-between">
          {sections.map((section, index) => (
            <button
              key={index}
              type="button"
              className={cn(
                'relative h-16 w-max cursor-pointer bg-white px-16 text-sm whitespace-nowrap text-neutral-300 transition-colors',
                {
                  'aria-[current="step"]:text-brand-primary': !isStatic,
                }
              )}
              onClick={() => scrollIntoView(section)}
              ref={(node) => {
                if (node && checkpointButtons.current) {
                  checkpointButtons.current[index] = node;

                  return () => {
                    delete checkpointButtons.current[index];
                  };
                }
              }}
            >
              {section.title}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default Timeline;
